import { createAsyncThunk, createSlice } from "@reduxjs/toolkit"
import GroupsService from "services/GroupsService"

// Async Thunks
export const getGroups = createAsyncThunk(
  "groups/getAll",
  async (params = {}, { rejectWithValue }) => {
    try {
      const response = await GroupsService.getGroups(params)
      const payload = response.data || {}
      return {
        groups: payload.data || [],
        totalPages:
          payload.pages ??
          payload.totalPages ??
          Math.ceil((payload.totalItems || 0) / (params.limit || 20)),
        currentPage: payload.page ?? params.page ?? 1,
        pageSize: payload.limit ?? params.limit ?? 20,
        totalItems: payload.totalItems ?? payload.count ?? 0,
      }
    } catch (error) {
      // ex. status invalide => 400 avec la liste des valeurs autorisées
      return rejectWithValue(
        error.response?.data?.error ||
          error.response?.data?.message ||
          error.message ||
          "Erreur",
      )
    }
  },
)

export const getGroupById = createAsyncThunk(
  "groups/getGroupById",
  async (id, { rejectWithValue }) => {
    try {
      const response = await GroupsService.getGroupById(id)
      return response.data.group
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || "Erreur")
    }
  },
)

export const getTrainingGroups = createAsyncThunk(
  "groups/getTrainingGroups",
  async (trainingId, { rejectWithValue }) => {
    try {
      const response = await GroupsService.getTrainingGroupsById(trainingId)
      return response.data.groups // Supposant que l'API renvoie { groups: [...] }
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || "Erreur")
    }
  },
)

export const getSupportCourseGroups = createAsyncThunk(
  "groups/getSupportCourseGroups",
  async (courseId, { rejectWithValue }) => {
    try {
      const response = await GroupsService.getSupportCourseGroupsById(courseId)
      return response.data.groups // Supposant que l'API renvoie { groups: [...] }
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || "Erreur")
    }
  },
)

export const createTrainingGroup = createAsyncThunk(
  "groups/createTrainingGroup",
  async (data, { rejectWithValue }) => {
    try {
      const response = await GroupsService.createTrainingGroup(data)
      return response.data.group
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || "Erreur")
    }
  },
)

export const createSupportCourseGroup = createAsyncThunk(
  "groups/createSupportCourseGroup",
  async (data, { rejectWithValue }) => {
    try {
      const response = await GroupsService.createSupportCourseGroup(data)
      return response.data.group
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || "Erreur")
    }
  },
)

export const deleteGroup = createAsyncThunk(
  "groups/deleteGroup",
  async (id, { rejectWithValue }) => {
    try {
      await GroupsService.deleteGroup(id)
      return id
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || "Erreur")
    }
  },
)

export const updateGroupStudents = createAsyncThunk(
  "groups/updateStudents",
  async ({ groupId, data }, { rejectWithValue }) => {
    try {
      const response = await GroupsService.updateGroupStudents(groupId, data)
      return response.data.updatedGroup
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || "Erreur")
    }
  },
)

export const addFormerToGroup = createAsyncThunk(
  "groups/addFormer",
  async ({ groupId, formateurId, paymentType, paymentValue }, { rejectWithValue }) => {
    try {
      const response = await GroupsService.addFormerToGroup(groupId, {
        formateurId,
        paymentType,
        paymentValue,
      })
      return response.data.group
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || "Erreur")
    }
  },
)

export const deleteFormerFromGroup = createAsyncThunk(
  "groups/deleteFormer",
  async ({ groupId, formateurId }, { rejectWithValue }) => {
    try {
      await GroupsService.deleteFormerById(groupId, { formateurId })
      return { groupId, formateurId }
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || "Erreur")
    }
  },
)

export const toggleGroupFinished = createAsyncThunk(
  "groups/toggleFinished",
  async (groupId, { rejectWithValue }) => {
    try {
      const response = await GroupsService.toggleFinished(groupId)
      return response.data.group
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || "Erreur")
    }
  },
)

export const updateGroupName = createAsyncThunk(
  "groups/updateName",
  async ({ groupId, groupName }, { rejectWithValue }) => {
    console.log("groupname inside redux thunk:",groupName)
    try {
      const response = await GroupsService.updateGroupName(groupId, groupName);
      return response.data; 
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || "Erreur lors de la mise à jour");
    }
  }
);

const initialState = {
  groups: [],
  trainingGroups: [],
  supportCourseGroups: [],
  group: null,
  loading: false,
  error: null,
  deletingId: null,
  totalPages: 1,
  currentPage: 1,
  pageSize: 20,
  totalItems: 0,
}

const groupsSlice = createSlice({
  name: "groups",
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null
    },
    clearGroup: (state) => {
      state.group = null
    },
  },
  extraReducers: {
    // getGroups
    [getGroups.pending]: (state) => {
      state.loading = true
    },
    [getGroups.fulfilled]: (state, { payload }) => {
      state.groups = payload.groups
      state.loading = false
      state.error = null
      state.totalPages = payload.totalPages
      state.currentPage = payload.currentPage
      state.pageSize = payload.pageSize
      state.totalItems = payload.totalItems
    },
    [getGroups.rejected]: (state, { payload }) => {
      state.error = payload
      state.loading = false
    },

    // getGroupById
    [getGroupById.pending]: (state) => {
      state.loading = true
      state.error = null
    },
    [getGroupById.fulfilled]: (state, { payload }) => {
      state.group = {
        ...payload,
        isForTraining: !!payload.formationId, // Recalculez isForTraining ici
      }
      state.loading = false
    },
    [getGroupById.rejected]: (state, { payload }) => {
      state.loading = false
      state.error = payload
      state.group = null
    },

    // getTrainingGroups
    [getTrainingGroups.pending]: (state) => {
      state.loading = true
      state.error = null
    },
    [getTrainingGroups.fulfilled]: (state, { payload }) => {
      state.trainingGroups = payload
      state.loading = false
      state.error = null
    },
    [getTrainingGroups.rejected]: (state, { payload }) => {
      state.trainingGroups = []
      state.loading = false
      state.error = payload
    },

    // getSupportCourseGroups
    [getSupportCourseGroups.pending]: (state) => {
      state.loading = true
      state.error = null
    },
    [getSupportCourseGroups.fulfilled]: (state, { payload }) => {
      state.supportCourseGroups = payload
      state.loading = false
      state.error = null
    },
    [getSupportCourseGroups.rejected]: (state, { payload }) => {
      state.supportCourseGroups = []
      state.loading = false
      state.error = payload
    },

    [createTrainingGroup.pending]: (state) => {
      state.loading = true
      state.error = null
    },
    [createTrainingGroup.fulfilled]: (state, { payload }) => {
      // Ajouter en tête de la liste générale
      state.groups.unshift(payload)
      // Ajouter en tête de la liste spécifique de la formation
      state.trainingGroups.unshift(payload)
      state.loading = false
      state.error = null
    },
    [createTrainingGroup.rejected]: (state, { payload }) => {
      state.error = payload
      state.loading = false
    },

    // createSupportCourseGroup
    [createSupportCourseGroup.pending]: (state) => {
      state.loading = true
    },
    [createSupportCourseGroup.fulfilled]: (state, { payload }) => {
      state.groups.unshift(payload)
      state.loading = false
      state.error = null
    },
    [createSupportCourseGroup.rejected]: (state, { payload }) => {
      state.error = payload
      state.loading = false
    },

    // deleteGroup
    [deleteGroup.pending]: (state, { meta }) => {
      state.loading = true
      state.deletingId = meta.arg
    },
    [deleteGroup.fulfilled]: (state, { payload }) => {
      state.groups = state.groups.filter((group) => group._id !== payload)
      state.loading = false
      state.deletingId = null
      state.error = null
    },
    [deleteGroup.rejected]: (state, { payload }) => {
      state.error = payload
      state.deletingId = null
      state.loading = false
    },

    // updateGroupStudents
    [updateGroupStudents.pending]: (state) => {
      state.loading = true
      state.error = null
    },
    [updateGroupStudents.fulfilled]: (state, { payload }) => {
      // Conserver les formers existants
      const existingGroup = state.groups.find((g) => g._id === payload._id)
      const updatedGroup = {
        ...payload,
        formers: existingGroup?.formers || [],
      }
      // Mettre à jour le groupe dans tous les états nécessaires
      if (state.group?._id === payload._id) {
        state.group = updatedGroup
      }
      state.groups = state.groups.map((group) =>
        group._id === payload._id ? updatedGroup : group,
      )

      if (updatedGroup.isForTraining) {
        state.trainingGroups = state.trainingGroups.map((group) =>
          group._id === payload._id ? updatedGroup : group,
        )
      } else {
        state.supportCourseGroups = state.supportCourseGroups.map((group) =>
          group._id === payload._id ? updatedGroup : group,
        )
      }
      state.loading = false
    },
    [updateGroupStudents.rejected]: (state, { payload }) => {
      state.error = payload
      state.loading = false
    },

    // addFormerToGroup
    [addFormerToGroup.pending]: (state) => {
      state.loading = true
      state.error = null
    },
    [addFormerToGroup.fulfilled]: (state, { payload }) => {
      // Mettre à jour group
      state.group = payload

      // Mettre à jour groups
      state.groups = state.groups.map((group) =>
        group._id === payload._id ? payload : group,
      )

      // Mettre à jour trainingGroups si c'est un groupe de formation
      if (payload.isForTraining) {
        state.trainingGroups = state.trainingGroups.map((group) =>
          group._id === payload._id ? payload : group,
        )
      }
      // Mettre à jour supportCourseGroups si c'est un groupe de soutien
      else {
        state.supportCourseGroups = state.supportCourseGroups.map((group) =>
          group._id === payload._id ? payload : group,
        )
      }

      state.loading = false
      state.error = null
    },
    [addFormerToGroup.rejected]: (state, { payload }) => {
      state.error = payload
      state.loading = false
    },

    // deleteFormerFromGroup
    [deleteFormerFromGroup.pending]: (state) => {
      state.loading = true
    },
    [deleteFormerFromGroup.fulfilled]: (state, { payload }) => {
      if (state.group && state.group._id === payload.groupId) {
        state.group.formers = state.group.formers.filter(
          (former) => former.formateurId !== payload.formerId,
        )
      }
      state.loading = false
      state.error = null
    },
    [deleteFormerFromGroup.rejected]: (state, { payload }) => {
      state.error = payload
      state.loading = false
    },

    // toggleGroupFinished
    [toggleGroupFinished.fulfilled]: (state, { payload }) => {
      if (state.group?._id === payload._id) {
        state.group = payload
      }
      state.groups = state.groups.map((group) =>
        group._id === payload._id ? payload : group,
      )
      if (payload.isForTraining) {
        state.trainingGroups = state.trainingGroups.map((group) =>
          group._id === payload._id ? payload : group,
        )
      } else {
        state.supportCourseGroups = state.supportCourseGroups.map((group) =>
          group._id === payload._id ? payload : group,
        )
      }
      state.loading = false
    },
  },
})

export const { clearError, clearGroup } = groupsSlice.actions

export default groupsSlice.reducer
